import { ListFilterScreenProps } from "./useFilters";
import { Flex } from "styled-system/jsx";
import { useGeocoding } from "shared/hooks";
import { Combobox, Input } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { GeocodingFeature } from "@mapbox/search-js-core";
import { MapboxMap } from "shared/components";
import { MarkerDragEvent } from "react-map-gl";

export const LocationFilter = ({
  filters,
  onUpdateFilters,
}: ListFilterScreenProps) => {
  const { t } = useTranslation("core");
  const locationFilter = filters["location"];

  const handleAddressSelect = (foundSuggestion: GeocodingFeature) => {
    onUpdateFilters({
      location: {
        ...locationFilter,
        address: foundSuggestion.properties.full_address,
        longitude: foundSuggestion.geometry.coordinates[0],
        latitude: foundSuggestion.geometry.coordinates[1],
      },
    });
  };

  const handleMarkerDrag = (e: MarkerDragEvent) => {
    onUpdateFilters({
      location: {
        ...locationFilter,
        longitude: e.lngLat.lng,
        latitude: e.lngLat.lat,
      },
    });
  };

  const {
    suggestionsMappedToCombobox,
    mapRef,
    marker,
    handleMarkerDragEnd,
    handleSelectAddress,
    getMapCoordinates,
  } = useGeocoding(
    locationFilter.address,
    { lng: locationFilter.longitude ?? 0, lat: locationFilter.latitude ?? 0 },
    handleAddressSelect,
    handleMarkerDrag,
  );

  return (
    <Flex flexDir={"column"} gap={300}>
      <MapboxMap
        mapRef={mapRef}
        mapCoordinates={getMapCoordinates()}
        markerLng={marker.lng}
        markerLat={marker.lat}
        markerOnDragEnd={handleMarkerDragEnd}
        markerDraggable
        height={246}
      />
      <Flex flexDir={"column"} gap={150}>
        <Combobox.Root
          inputValue={locationFilter.address}
          label={t("FILTER.LOCATION.FIELDS.LOCATION_SEARCH")}
          placeholder={t("FILTER.LOCATION.FIELDS.LOCATION_SEARCH")}
          hint={t("FILTER.LOCATION.FIELDS.LOCATION_HELPER_TEXT")}
          items={suggestionsMappedToCombobox}
          onInputValueChange={(d) => {
            onUpdateFilters({
              location: { ...locationFilter, address: d.inputValue },
            });
            handleSelectAddress(d);
          }}
        >
          <Combobox.ItemGroup>
            {suggestionsMappedToCombobox.length === 0 && (
              <Combobox.ItemGroupLabel>
                {t("SELECT_ADDRESS.AUTOCOMPLETE_HINT")}
              </Combobox.ItemGroupLabel>
            )}
            {suggestionsMappedToCombobox.map((item) => (
              <Combobox.Item key={item.value} item={item} {...item} />
            ))}
          </Combobox.ItemGroup>
        </Combobox.Root>
        <Flex gap={150}>
          <Input
            type={"number"}
            label={t("SELECT_ADDRESS.LONGITUDE")}
            placeholder={t("SELECT_ADDRESS.LONGITUDE")}
            value={locationFilter.longitude ?? ""}
            onChange={(e) =>
              onUpdateFilters({
                location: {
                  ...locationFilter,
                  longitude: Number(e.target.value),
                },
              })
            }
          />
          <Input
            type={"number"}
            label={t("SELECT_ADDRESS.LATITUDE")}
            placeholder={t("SELECT_ADDRESS.LATITUDE")}
            value={locationFilter.latitude ?? ""}
            onChange={(e) =>
              onUpdateFilters({
                location: {
                  ...locationFilter,
                  latitude: Number(e.target.value),
                },
              })
            }
          />
        </Flex>
        <Input
          type={"number"}
          label={t("FILTER.LOCATION.FIELDS.RADIUS")}
          placeholder={t("FILTER.LOCATION.FIELDS.RADIUS")}
          value={locationFilter.radius}
          hint={t("FILTER.LOCATION.FIELDS.RADIUS_HELPER_TEXT")}
          onChange={(e) =>
            onUpdateFilters({
              location: {
                ...locationFilter,
                radius: Number(e.target.value),
              },
            })
          }
        />
      </Flex>
    </Flex>
  );
};
